import { Button, ButtonProps } from '@mantine/core';
import classes from './StyledButton.module.css';
import { MouseEvent, ReactNode } from 'react';

type ButtonSizes = 'sm' | 'md';

export type ButtonVariants =
  | 'primary'
  | 'outline'
  | 'subtle'
  | 'delete'
  | 'grey'
  | 'subtleIcon'
  | 'icon';

type ButtonPropsWithoutVariantAndSize = Omit<ButtonProps, 'variant' | 'size'>;

type StyledButtonBaseProps = ButtonPropsWithoutVariantAndSize & {
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  size?: ButtonSizes;
  children?: ReactNode;
};

type AccessibleIconButtonName =
  | {
    'aria-label': string;
    'aria-labelledby'?: string;
  }
  | {
    'aria-label'?: string;
    'aria-labelledby': string;
  };

type StyledButtonProps =
  | (StyledButtonBaseProps & {
    variant?: Exclude<ButtonVariants, 'icon' | 'subtleIcon'>;
  })
  | (StyledButtonBaseProps & AccessibleIconButtonName & {
    variant: 'icon' | 'subtleIcon';
  });

const BUTTON_CLASSES = {
  primary: {
    sm: classes.primarySm,
    md: classes.primaryMd,
  },
  outline: {
    sm: classes.outlineSm,
    md: classes.outlineMd,
  },
  subtle: {
    sm: classes.subtleSm,
    md: classes.subtleMd,
  },
  delete: {
    sm: classes.deleteSm,
    md: classes.deleteMd,
  },
  grey: {
    sm: classes.greySm,
    md: classes.greyMd,
  },
  subtleIcon: {
    sm: classes.subtleIcon,
    md: classes.subtleIcon,
  },
  icon: {
    sm: classes.icon,
    md: classes.icon,
  },
};

const StyledButton = ({
  size = 'md',
  variant = 'primary',
  children,
  ...props
}: StyledButtonProps) => {
  const className = BUTTON_CLASSES[variant][size];

  const loaderProps = { ...{ color: 'var(--velos-brand-pink)' }, ...props.loaderProps };

  return (
    <Button classNames={{ root: className }} {...props} loaderProps={loaderProps}>
      {children}
    </Button>
  );
};

export default StyledButton;

export type { StyledButtonProps };
